/**
 * Live page-header background viewer — standalone frontend entry.
 *
 * Upgrades any `[data-page-bg]` element into the live WebGL noise/seam background
 * (PageBgEngine), reproduced from the page's header image plus the selected
 * "Hintergrund-Stil" preset's settings. Used by the page header
 * (template-parts/detail/page/header.php). Unlike the event styler this is
 * LIVE-ONLY — there is no baked PNG, and there is no poster <img> fallback: the
 * raw, unstyled source must never flash, so when this bails only the page bg
 * shows (no WebGL / reduced motion / missing src).
 *
 * This is its OWN top-level Vite entry (built as an ES module) and is enqueued as
 * `type="module"` only on pages that actually have a header background — see
 * library/page-bg.php (glunz_page_has_bg_header) and library/enqueue-scripts.php.
 * It is kept OUT of the classic `main` bundle on purpose: pulling the (ESM,
 * code-split) engine into dist/main.js's graph would turn it into a module and
 * break the whole bundle in production. Mirrors src/image-styler-view.js.
 *
 * Motion animates continuously while the header is on-screen; the cursor adds
 * local distortion on top (lagged by the preset's `mouseLag`). It pauses when
 * scrolled off-screen or when the preset's `freeze` is on, and renders nothing
 * (only the page bg) when WebGL is unavailable or the visitor prefers reduced motion.
 */

import { PageBgEngine } from './page-bg/engine.js'

const SELECTOR = '[data-page-bg]'

// Cap the live render rate. The noise animates slowly (animSpeed ~0.02–0.03), so
// 30fps is visually indistinguishable from 60/120 while roughly halving the
// continuous GPU/battery cost of this always-on full-bleed background.
const MIN_FRAME_MS = 1000 / 30

// Cap the drawing-buffer resolution below the engine's 2× DPR. This bg is
// full-viewport and always on, so 1.5× roughly halves fragment cost on retina
// screens with only a slight softening of the organic noise. Weak devices
// (≤4 GB RAM or ≤4 cores) start at 1.0× to cut fragment cost / heat further.
const LIVE_MAX_DPR =
  (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
  (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ? 1.0 : 1.5

// Static-fallback gate: reduced-motion OR reduced-data / Save-Data → no live bg
// (weak/metered sessions just show the page background).
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
  window.matchMedia('(prefers-reduced-data: reduce)').matches ||
  (navigator.connection && navigator.connection.saveData === true) ||
  document.body.classList.contains('reduce-motion') // a11y quiet mode (src/js/accessibility.js)

// el -> { setVisible(bool) | null }. Lets the IntersectionObserver drive
// pause/resume even for visibility changes that land mid-initialisation.
const controllers = new WeakMap()

function onIntersect(entries) {
  for (const entry of entries) {
    const ctrl = controllers.get(entry.target)
    if (ctrl) {
      if (ctrl.setVisible) ctrl.setVisible(entry.isIntersecting)
      continue
    }
    if (!entry.isIntersecting) continue // wait until it first scrolls into view
    const placeholder = { setVisible: null }
    controllers.set(entry.target, placeholder)
    initBg(entry.target, placeholder)
  }
}

async function initBg(el, ctrl) {
  // 1. Settings + source — bail (no background renders) if either is missing/bad.
  let settings
  try { settings = JSON.parse(el.dataset.pageBgSettings || '') }
  catch { return }
  if (!settings || typeof settings !== 'object') return
  const src = el.dataset.pageBgSrc
  if (!src) return
  if (prefersReducedMotion()) return // re-check: the a11y class may settle after load

  // 2. WebGL engine — bail (leave poster) if unavailable.
  const canvas = document.createElement('canvas')
  canvas.className = 'page-header-bg-canvas'
  canvas.setAttribute('aria-hidden', 'true')
  el.appendChild(canvas)

  let engine
  try { engine = new PageBgEngine(canvas) }
  catch { canvas.remove(); return } // WebGL unsupported → poster img stays
  engine.maxDpr = LIVE_MAX_DPR

  // 3. Per-instance run state.
  const wrapper = el.closest('.page-header-bg-wrapper') || el
  const target = { x: 0.5, y: 0.5 } // latest pointer (0..1, y flipped to GL)
  const smooth = { x: 0.5, y: 0.5 } // lagged cursor actually fed to the shader
  let visible = true
  let running = false
  let started = false
  let revealed = false
  let pendingReveal = false
  let rafId = 0
  let t0 = 0
  let elapsed = 0 // animation clock in seconds; only advances while running
  let lastDraw = 0 // performance.now() of the last drawn frame (fps cap)

  const wantsRun = () => started && visible && !engine.state.freeze && !engine._lost
  // GPU context lost → stop and reveal the page background (no poster for this effect).
  const onLost = () => {
    stop()
    wrapper.classList.remove('is-bg-live')
    el.classList.remove('is-bg-live')
    canvas.remove()
  }

  // Reveal the styled header background — the live canvas AND the colour overlay
  // — as ONE coordinated fade, and only once the WHOLE page has finished loading
  // (window `load`). Bringing them in together (rather than the image first and
  // the colour washing in later) means the accent colour never appears as a
  // separate flash over an already-visible background; holding until `load` means
  // it never appears over a still-loading page. Both layers start hidden (CSS).
  const reveal = () => {
    if (revealed || engine._lost) return
    revealed = true
    el.classList.add('is-bg-live')       // live canvas fades in …
    wrapper.classList.add('is-bg-live')  // … together with the colour overlay
  }
  const revealIfReady = () => {
    // Wait for the first painted frame (canvas has real dimensions) so the reveal
    // shows the rendered image, not a blank box; then hold until the page loads.
    if (revealed || pendingReveal || canvas.width <= 0 || canvas.height <= 0) return
    pendingReveal = true
    if (document.readyState === 'complete') reveal()
    else window.addEventListener('load', reveal, { once: true })
  }

  const drawFrame = (timeSec) => {
    const lag = engine.state.mouseLag || 0.07
    smooth.x += (target.x - smooth.x) * lag
    smooth.y += (target.y - smooth.y) * lag
    engine.render(timeSec, smooth)
    revealIfReady()
  }
  const tick = (now) => {
    if (engine._lost) { onLost(); return } // GPU context lost → reveal page bg
    rafId = requestAnimationFrame(tick)
    if (now - lastDraw < MIN_FRAME_MS) return // fps cap
    lastDraw = now
    drawFrame(elapsed + (now - t0) / 1000)
  }
  const start = () => {
    if (running) return
    running = true
    t0 = performance.now()
    rafId = requestAnimationFrame(tick)
  }
  const stop = () => {
    if (!running) return
    running = false
    cancelAnimationFrame(rafId)
    elapsed += (performance.now() - t0) / 1000 // freeze the clock where it stopped
  }
  const renderStatic = () => drawFrame(elapsed) // one frozen frame (idle / paused / resize)
  const update = () => { if (wantsRun()) start(); else if (running) { stop(); renderStatic() } }

  ctrl.setVisible = (v) => { visible = v; update() }

  // 4. Cover-fit override: the shader fits CONTAIN, but the header is cover.
  // Sizing the canvas CSS box to cover (at the image AR) makes containUV a no-op
  // so the image fills the box; the wrapper's overflow:hidden crops it.
  engine.fitCanvasToImage = () => {
    const pw = el.clientWidth
    const ph = el.clientHeight
    if (!engine.loadedImg || pw <= 0 || ph <= 0) return
    const imgAR = engine.imgAspect
    let cssW, cssH
    if (pw / ph > imgAR) { cssW = pw; cssH = pw / imgAR }
    else { cssH = ph; cssW = ph * imgAR }
    canvas.style.width = cssW + 'px'
    canvas.style.height = cssH + 'px'
  }

  // 5. State + image.
  engine.setState(settings)
  const img = new Image()
  img.src = src
  try {
    await img.decode()
    await engine.setImage(img)
  } catch {
    canvas.remove() // styler image failed → no fallback, only the page bg shows
    ctrl.setVisible = null
    return
  }

  // 6. Pointer (window-level — the header is fixed, behind content, so element
  // listeners never fire). Mapping the cursor to canvas coords means its local
  // distortion naturally fades as it moves off the header.
  // Cache the canvas rect so pointermove doesn't force a layout (getBoundingClientRect)
  // on EVERY move. The header wrapper is position:fixed, so the rect is stable across
  // scroll — it only changes on resize / re-fit, where we refresh it.
  let rect = canvas.getBoundingClientRect()
  const refreshRect = () => { rect = canvas.getBoundingClientRect() }

  window.addEventListener('pointermove', (e) => {
    if (rect.width > 0 && rect.height > 0) {
      target.x = (e.clientX - rect.left) / rect.width
      target.y = 1 - (e.clientY - rect.top) / rect.height
    }
  })

  new ResizeObserver(() => {
    engine.fitCanvasToImage()
    refreshRect()
    if (!running) renderStatic() // keep the frozen/idle frame correct after a resize
  }).observe(wrapper)

  // 7. Show a static frame immediately, then let the idle-motion policy decide.
  engine.fitCanvasToImage()
  refreshRect()
  started = true
  renderStatic()
  update()
}

if (document.body && !prefersReducedMotion()) {
  const nodes = document.querySelectorAll(SELECTOR)
  if (nodes.length) {
    const io = new IntersectionObserver(onIntersect, { rootMargin: '200px' })
    nodes.forEach((n) => io.observe(n))
  }
}
